import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
// import Select from 'react-select';
import { getValue } from '@utils/lodash';

const datePickerStyles = `
  .date-picker-with-border .react-datepicker__input-container input {
    border: 1px solid #ccc !important;
    border-radius: 4px !important;
    padding: 8px 12px !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }

  .date-picker-with-border .react-datepicker__input-container input:focus {
    border-color: #007bff !important;
    outline: none !important;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
  }
`;

interface ExpenseExportPopupProps {
  isOpen: boolean;
  handleCancelModal: () => void;
  handleExport: (data: any) => void;
  heading?: string;
  showApprovalStatus?: boolean;
  showExcludeEmptyColumns?: boolean;
  showApprovalDateRange?: boolean;
}

function ExpenseExportPopup({
  isOpen,
  handleCancelModal,
  handleExport,
  heading = 'Export Expenses Summary',
  showApprovalStatus = true,
  showExcludeEmptyColumns = false,
  showApprovalDateRange = false,
}: ExpenseExportPopupProps) {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const urlParams = Object.fromEntries(urlSearchParams.entries());

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [approvalFromDate, setApprovalFromDate] = useState<Date | null>(null);
  const [approvalToDate, setApprovalToDate] = useState<Date | null>(null);
  // const [approvalStatus, setApprovalStatus] = useState<string | null>(null);
  const [isEmptyColumnsExcluded, setIsEmptyColumnsExcluded] =
    useState(true);

  useEffect(() => {
    if (!isOpen) {
      setFromDate(null);
      setToDate(null);
      setApprovalFromDate(null);
      setApprovalToDate(null);
      // setApprovalStatus(null);
      setIsEmptyColumnsExcluded(true);
    }
  }, [isOpen]);

  const formatDateToYYYYMMDD = (date: Date | null) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // const approvalStatusOptions = [
  //   { value: 'approved', label: 'Approved' },
  //   { value: 'pending', label: 'Pending' },
  //   { value: 'rejected', label: 'Rejected' },
  //   { value: 'awaiting_approval', label: 'Awaiting Approval' },
  // ];

  const handleSubmit = () => {
    const payload: any = {};
    if (fromDate) payload.start_date = formatDateToYYYYMMDD(fromDate);
    if (toDate) payload.end_date = formatDateToYYYYMMDD(toDate);
    if (showApprovalDateRange && approvalFromDate) {
      payload.approved_at_from = formatDateToYYYYMMDD(approvalFromDate);
    }
    if (showApprovalDateRange && approvalToDate) {
      payload.approved_at_to = formatDateToYYYYMMDD(approvalToDate);
    }
    // if (showApprovalStatus && approvalStatus) {
    //   payload.approval_status = approvalStatus;
    // }
    if (showExcludeEmptyColumns) {
      payload.non_empty_columns_excluded = isEmptyColumnsExcluded;
    }

    handleExport({
      ...payload,
      ...(getValue(urlParams, `approval_status`, null)
        ? { approval_status: getValue(urlParams, `approval_status`, '') }
        : {}),
    });
  };

  return (
    <>
      <style>{datePickerStyles}</style>
      <Modal isOpen={isOpen} toggle={handleCancelModal} size="md">
        <ModalHeader toggle={handleCancelModal}>{heading}</ModalHeader>
        <ModalBody>
          <div className="row">
            <div className="col-md-6 mb-3 date-picker-with-border">
              <label className="form-label">From Date</label>
              <DatePicker
                selected={fromDate}
                onChange={(date: Date | null) => setFromDate(date)}
                dateFormat="d MMMM, yyyy"
                placeholderText="Select From Date"
                className="custom-form-control form-control"
                maxDate={toDate || undefined}
              />
            </div>
            <div className="col-md-6 mb-3 date-picker-with-border">
              <label className="form-label">To Date</label>
              <DatePicker
                selected={toDate}
                onChange={(date: Date | null) => setToDate(date)}
                dateFormat="d MMMM, yyyy"
                placeholderText="Select To Date"
                className="custom-form-control form-control "
                minDate={fromDate || undefined}
              />
            </div>
            {/* {showApprovalStatus && (
              <div className="col-md-6 mb-3">
                <label className="form-label">Approval Status</label>
                <Select
                  classNamePrefix="select"
                  options={approvalStatusOptions}
                  placeholder="Select Approval Status"
                  value={
                    approvalStatus
                      ? approvalStatusOptions.find(
                          (o) => o.value === approvalStatus,
                        )
                      : null
                  }
                  onChange={(option) =>
                    setApprovalStatus(option ? option.value : null)
                  }
                  isClearable
                />
              </div>
            )} */}

            {showApprovalDateRange && (
              <>
                <div className="col-md-6 mb-3 date-picker-with-border">
                  <label className="form-label">Approval From Date</label>
                  <DatePicker
                    selected={approvalFromDate}
                    onChange={(date: Date | null) =>
                      setApprovalFromDate(date)
                    }
                    dateFormat="d MMMM, yyyy"
                    placeholderText="Select Approval From Date"
                    className="custom-form-control form-control"
                    maxDate={approvalToDate || undefined}
                  />
                </div>
                <div className="col-md-6 mb-3 date-picker-with-border">
                  <label className="form-label">Approval To Date</label>
                  <DatePicker
                    selected={approvalToDate}
                    onChange={(date: Date | null) => setApprovalToDate(date)}
                    dateFormat="d MMMM, yyyy"
                    placeholderText="Select Approval To Date"
                    className="custom-form-control form-control "
                    minDate={approvalFromDate || undefined}
                  />
                </div>
              </>
            )}

            {showExcludeEmptyColumns && (
              <div className="col-md-12 mb-3">
                <div className="form-check">
                  <input
                    id="exclude-empty-columns"
                    type="checkbox"
                    className="form-check-input"
                    checked={isEmptyColumnsExcluded}
                    onChange={(e) =>
                      setIsEmptyColumnsExcluded(e.target.checked)
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor="exclude-empty-columns"
                  >
                    Exclude empty columns
                  </label>
                </div>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            className="ascent-button ascent-button--header-buttons ascent-button--primary"
            onClick={handleCancelModal}
          >
            Cancel
          </button>
          <button
            className="ascent-button ascent-button--header-buttons ascent-button--secondary"
            onClick={handleSubmit}
          >
            Download
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default ExpenseExportPopup;
